import { useEffect, useState } from 'react';
import { Button, Dialog, Input } from '@dreamtree-org/twreact-ui';
import { CalendarDays } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { useAppToast } from '@/lib/toast';
import { dateInput, errorMessage } from '@/lib/format';
import { DEFAULT_POLICY, expiryMeta, expiryPhrase, previewState } from '@/lib/expiry';
import ProductImage from './ProductImage';

/**
 * "Update the expiry date" without leaving the list.
 *
 * This is the single most-repeated action in the app — a clerk works down a
 * shelf with a scanner in one hand — so it is one dialog, one field, and a Save
 * that closes it. Opening the full product form to change one date would make
 * the common case the slow one.
 *
 * The quick presets exist for the same reason: received stock is almost always
 * dated in round months, and "+6 months" is one tap instead of a date picker.
 */
const PRESETS = [
  { label: '+1 week', days: 7 },
  { label: '+1 month', months: 1 },
  { label: '+3 months', months: 3 },
  { label: '+6 months', months: 6 },
  { label: '+1 year', months: 12 },
];

function shift({ days, months }) {
  const d = new Date();
  if (days) d.setDate(d.getDate() + days);
  if (months) d.setMonth(d.getMonth() + months);
  return dateInput(d);
}

export default function ExpiryEditor({ product, isOpen, onClose, onSaved, policy = DEFAULT_POLICY }) {
  const { fetchApi } = useAppApi();
  const toast = useAppToast();
  const [value, setValue] = useState('');
  const [batch, setBatch] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Re-seed whenever a different row opens the dialog.
  useEffect(() => {
    if (!isOpen || !product) return;
    setValue(dateInput(product.expiry_date));
    setBatch(product.batch_no || '');
    setErr('');
  }, [isOpen, product]);

  if (!product) return null;

  // Preview the state the chosen date WILL produce, before saving — against
  // THIS warehouse's thresholds, so the preview cannot promise something the
  // server would then contradict.
  const preview = (() => {
    if (!value) return null;
    const ms = new Date(`${value}T00:00:00`).getTime() - new Date(new Date().toDateString()).getTime();
    return Math.round(ms / 86400000);
  })();
  const previewMeta = preview == null ? null : expiryMeta(previewState(preview, policy));

  const save = async () => {
    if (!value) {
      setErr('Choose an expiry date');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const res = await fetchApi('productUpdateExpiry', {
        id: product.id,
        expiry_date: value,
        batch_no: batch,
      });
      toast.success('Expiry updated', `${product.name} now expires ${dateInput(value)}.`);
      onSaved && onSaved((res && res.data) || null);
      onClose();
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not update the expiry date'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Update expiry date"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving} leftIcon={<CalendarDays size={16} />}>
            Save date
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-2)] p-3">
          <ProductImage src={product.image_url} name={product.name} className="h-14 w-14 shrink-0" />
          <div className="min-w-0">
            <div className="truncate font-semibold text-[var(--app-text)]">{product.name}</div>
            <div className="truncate text-xs text-[var(--app-muted)]">
              {product.sku}
              {product.brand ? ` · ${product.brand}` : ''}
            </div>
            <div className="mt-0.5 text-xs" style={{ color: expiryMeta(product.expiry_state).fg }}>
              Currently: {expiryPhrase(product.days_to_expiry)}
            </div>
          </div>
        </div>

        <Input
          label="New expiry date"
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={err || undefined}
          required
        />

        <div>
          <div className="mb-1.5 text-xs font-medium text-[var(--app-muted)]">Quick set from today</div>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => setValue(shift(p))}
                className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1 text-xs font-medium text-[var(--app-text)] transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <Input
          label="Batch number"
          value={batch}
          onChange={(e) => setBatch(e.target.value)}
          placeholder="Optional"
        />

        {previewMeta ? (
          <div
            className="rounded-xl border px-3 py-2 text-sm font-medium"
            style={{ color: previewMeta.fg, background: previewMeta.bg, borderColor: previewMeta.border }}
          >
            {expiryPhrase(preview)} — {previewMeta.blurb}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
